import React from 'react';
import { BaseFill } from './base-fill';
import {
    GradientType,
    IBackgroundFillInfo,
    IGradFill,
} from '@src/slide/domain/ui-model/element-modeles/property/fill/fill.interface';
import GradualPreset from '@framework/workbench/drawer/components/gradual-preset';
import {
    DirectList,
    getConfigByRect,
    getLinearDirection,
    getRectDirection,
    ShapeDirectionList,
} from '@editor/src/js/constants';
import {
    LINEAR_GRADIENT,
    RADIAL_GRADIENT,
    RECT_GRADIENT,
    SHAPE_GRADIENT,
} from '../../../../../fill/constants';
import i18n from '@i18next/i18n';
import { copy } from '@src/slide/infrastructure/module/independent/object-operation';
import { FormItemName } from '@framework/workbench/drawer/constants';
import type { AngleFrom, NumberInputFrom } from '@tencent/wb-ext-form';
import { alphaToOpacity } from '@framework/workbench/drawer/utils';
import {
    TDWVer5Type,
    TDWVer6Type,
} from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import { ITDColor, TDColor } from '@src/slide/domain/ui-model/element-modeles/property/color/td-color';
import ColorPickerContainer from '@src/js/common/ark-extensions/dc-components/color-picker/color-picker-container';
import { ColorPickerType } from '@src/js/common/ark-extensions/dc-components/color-picker/type';
import { GradualSetting } from '@tencent/drawing-drawer-components/lib/components/gradual-setting';
import { IGradualPoint as IDrawerGradualPoint } from '@tencent/drawing-drawer-components/lib/interface';
import { IGradualPoint } from '@framework/workbench/drawer/ui/fill/gradual-fill/gradual-fill-feature.interface';

const START_COLOR = '#FFF2CC';
const END_COLOR = '#FFB700';
const pointArray = [
    {
        id: GradualSetting.startPoint, left: 0,
        bgColor: new TDColor({ rgbaStr: START_COLOR }), percent: 0,
    },
    {
        id: GradualSetting.endPoint,
        left: GradualSetting.gradientContentWidth - GradualSetting.pointWidth,
        bgColor: new TDColor({ rgbaStr: END_COLOR }),
        percent: 100,
    },
];
const FILL_DEFAULT_COLOR = '#ffffff';
const DEFAULT_GRADUAL_ANGLE = 135;

type IPoint = {
    bgColor: ITDColor;
    id: string;
    left: number;
    percent: number;
    circle?: boolean;
};

interface IGradValue {
    curGradualType: string;
    curDirect: any;
    gradualAngle: number;
    activePointId: string;
    pointArray: {
        id: string;
        left: number;
        bgColor: ITDColor;
        percent: number;
    }[]
    presetItem: {
        type: string;
        style: string;
        angle: number;
        list: {
            pos: number;
            color: string;
        }[];
    } | null | undefined;
    activeGradualColor: ITDColor;
    isChangeGradualDirection: boolean;
    shadeToTitle: number | undefined;
    rotateWithShape?: 0 | 1;
}

export class GradFill extends BaseFill {
    public value: IGradValue;
    public graduaTypes = [
        { id: LINEAR_GRADIENT, title: i18n.t('线性渐变') },
        { id: RADIAL_GRADIENT, title: i18n.t('径向渐变') },
    ];
    private directList = DirectList;
    private dropDom;
    private gradualFill;

    public constructor() {
        super();
        this.value = {
            curGradualType: LINEAR_GRADIENT,
            curDirect: this.directList[0],
            gradualAngle: DEFAULT_GRADUAL_ANGLE,  // 线性渐变角度
            activePointId: pointArray[0].id,
            pointArray,
            presetItem: GradualPreset.getPresetItemByStyle('gra-preset-3'),
            activeGradualColor: new TDColor({ rgbaStr: START_COLOR }),  // 渐变填充active的渐变点的color
            isChangeGradualDirection: false,
            shadeToTitle: 0,
            rotateWithShape: 1,
        };
    }

    public initPropsCb = (fillInfo?: IGradFill, fillColor?: IBackgroundFillInfo) => {
        const { curGradualAngle } = this.getGradualInfo(fillInfo);
        // 渐变填充角度
        this.value.gradualAngle = curGradualAngle ?? DEFAULT_GRADUAL_ANGLE;
        // 渐变填充颜色-透明度
        this.updateActivePoint(fillInfo);
        this.value.activeGradualColor = this.getActivePoint()?.bgColor || new TDColor({ rgbaStr: START_COLOR });
        this.value.shadeToTitle = fillColor?.shadeToTitle;
    };

    public renderGradualFill(gradFill?: IGradFill | null) {
        this.updateGradualFill(gradFill);
        return <GradualSetting
            pointArray={this.getPointArray()}
            activePointId={this.value.activePointId}
            updatePoint={this.handleUpdatePoint}
            getDropContent={() => <ColorPickerContainer
                colorPickerType={ColorPickerType.StandardPC}
                onColorChange={this.handlePaletteGradualColorChange}
                selectedTdColor={this.value.activeGradualColor}
            />}
        />;
    }

    public handleAngleChange(value: number, dragEnd?: boolean, from?: AngleFrom) {
        this.value.gradualAngle = value;
        const onlyChangeUI = this.isInputOnlyChangeUIWithDrag(dragEnd);
        this.value.presetItem = onlyChangeUI ? null : GradualPreset.compareWithPreset(this.gradualFill, value);
        this.confirmGradChoice(onlyChangeUI);
        this.tdwDrawerReportAngle(FormItemName.GRADUAL_FILL_ANGLE, from, value, onlyChangeUI);
    }

    public getGradualOpacity = (): number => {
        const { alpha } = this.value.activeGradualColor;
        return alphaToOpacity(alpha);
    };

    public handleGradualOpacityChange(opacity: number, dragEnd?: boolean, from?: NumberInputFrom) {
        this.value.activeGradualColor.updateOpacity(opacity);
        const onlyChangeUI = this.isInputOnlyChangeUIWithDrag(dragEnd);
        this.handleGradColorChange(this.value.activeGradualColor, false, onlyChangeUI);
        this.tdwDrawerReportNumberInput(FormItemName.GRADUAL_FILL_OPACITY, { ver6: opacity }, from, onlyChangeUI);
    }

    public handleGradualFillDirect = (value) => {
        this.value.curDirect = value;
        this.value.isChangeGradualDirection = true;
        this.confirmGradChoice();
        // 上报数据
        this.tdwDrawerReport(FormItemName.GRADUAL_FILL_DIRECTION, { ver5: value?.title });
    };

    public updateGradualFill = (gradFill?: IGradFill | null) => {
        this.gradualFill = gradFill;
        let pointList = this.value.pointArray;
        let activePointId = this.value.activePointId ? this.value.activePointId : GradualSetting.startPoint;
        if (!gradFill) return;
        const rotateWithShape = typeof (gradFill.rotateWithShape) === 'undefined' ? 1 : gradFill.rotateWithShape;
        const presetItem = GradualPreset.compareWithPreset(gradFill);
        if (gradFill.gsList instanceof Array) {
            pointList = gradFill.gsList.map((item, index) => this.transformPoint(index, gradFill, item));
            const activePointIndex = this.getActivePointIndex(this.value.pointArray, activePointId);
            activePointId = this.getActivePointId(pointList, activePointIndex);
        }
        const { curGradualType, curDirect } = this.getGradualInfo(gradFill);
        this.value = {
            ...this.value,
            activePointId,
            pointArray: pointList,
            curGradualType,
            curDirect,
            presetItem,
            rotateWithShape,
        };
    };

    public confirmGradChoice = (onlyChangeUI?: boolean) => {
        this.updateFillProps(this.getGradualProps(onlyChangeUI), onlyChangeUI);
        this.value.isChangeGradualDirection = false;
    };

    public getGradualProps = (onlyChangeUI?) => {
        const gsList = this.value.pointArray.map((item) => {
            const color = item.bgColor;
            const changeItem = { pos: item.percent, color, id: '' };
            if (onlyChangeUI) {  // 只改UI还要加上ID，防止for循环key警告
                changeItem.id = item.id;
            }
            return changeItem;
        });
        const gradFill: Partial<IGradFill> = {
            // @ts-ignore
            gsList,
            rotateWithShape: this.value.rotateWithShape,
        };

        if (this.value.curGradualType === LINEAR_GRADIENT) {  // 线性渐变
            const angle = this.value.isChangeGradualDirection ? this.value.curDirect.angle : this.value.gradualAngle;
            gradFill.lin = { ang: angle, scaled: 1 };
        } else {  // 其他渐变
            let path = GradientType.Radial;
            if (this.value.curGradualType === RECT_GRADIENT) {
                path = GradientType.Rect;
            } else if (this.value.curGradualType === SHAPE_GRADIENT) {
                path = GradientType.Shape;
            }
            const fillToRect = copy(this.value.curDirect.rect);
            const tileRect = copy(this.value.curDirect.tileRect);
            gradFill.pathGradient = { path, fillToRect };
            gradFill.tileRect = tileRect;
        }
        return { gradFill, shadeToTitle: this.value.shadeToTitle };
    };

    public getActivePoint = () => {
        const index = this.getActivePointIndex(
            this.value.pointArray,
            this.value.activePointId,
        );
        return this.value.pointArray[index];
    };

    private handleUpdatePoint = (id: string, pointArray: IDrawerGradualPoint[], onlyChangeUI: boolean) => {
        pointArray = pointArray.map((item) => {
            const bgColor = item.bgColor instanceof TDColor ? item.bgColor
                : new TDColor({ rgbaStr: item.bgColor.srgbColor });
            return {
                ...item,
                bgColor,
            };
        }) as IGradualPoint[];
        this.updateBackGroundGradualPoint(id, pointArray, onlyChangeUI);
        this.value.activePointId = id || this.value.activePointId;
        this.value.pointArray = (pointArray as IGradualPoint[]) || this.value.pointArray;
        this.value.presetItem = pointArray ? null : this.value.presetItem;
        if (pointArray) {
            this.value.presetItem = null;
            this.confirmGradChoice(onlyChangeUI);
        }
        // 获取当前active的渐变点的color
        this.value.activeGradualColor = this.getActivePoint().bgColor;
    };

    // 切换背景渐变填充渐变点时未触发刷新
    private updateBackGroundGradualPoint = (id: string, pointArray, onlyChangeUI: boolean) => {
        if (!this.isBackground() || onlyChangeUI || id === this.value.activePointId) return;
        const activePointValue = this.getActivePoint();
        const index = this.getActivePointIndex(pointArray, id);
        const activePointNew = pointArray[index];
        if (activePointValue.bgColor === activePointNew.bgColor) return;
        this.formService.$forceUpdate();
    };

    private updateActivePoint(gradFill?: IGradFill | null) {
        let pointList = this.value.pointArray;
        let activePointId = this.value.activePointId ? this.value.activePointId : GradualSetting.startPoint;
        if (!gradFill) return;
        if (gradFill.gsList instanceof Array) {
            pointList = gradFill.gsList.map((item, index) => this.transformPoint(index, gradFill, item));
            const activePointIndex = this.getActivePointIndex(this.value.pointArray, activePointId);
            activePointId = this.getActivePointId(pointList, activePointIndex);
        }
        this.value = {
            ...this.value,
            activePointId,
            pointArray: pointList,
        };
    }

    private transformPoint(index, gradFill, item) {
        const maxOffset = GradualSetting.gradientContentWidth - GradualSetting.pointWidth;
        let id = '';
        let percent = 0;
        if (index === 0) {
            id = GradualSetting.startPoint;
            percent = 0;
        } else if (index === gradFill.gsList.length - 1) {
            id = GradualSetting.endPoint;
            percent = 100;
        } else {
            id = `${GradualSetting.pointIndex}-gradual-arrow`;
            GradualSetting.pointIndex += 1;
        }

        percent = item ? item.pos : percent;
        const bgColor = item?.color ? item.color : new TDColor({ rgbaStr: FILL_DEFAULT_COLOR });

        const left = Math.round(percent / 100 * maxOffset);
        return {
            id, left, bgColor, percent,
        };
    }

    private getActivePointIndex = (pointArray: IPoint[], activePointId: string): number => {
        for (let i = 0; i < pointArray.length; i++) {
            if (pointArray[i].id === activePointId) {
                return i;
            }
        }
        return 0;
    };

    private getPointArray = () => this.value.pointArray;

    private getActivePointId = (pointArray, activePointIndex) => {
        let activePointId = GradualSetting.startPoint;
        if (activePointIndex >= 0 && activePointIndex < pointArray.length) {
            activePointId = pointArray[activePointIndex].id;
        } else if (pointArray.length > 2) {  // 初始化起始点和结束点
            activePointId = pointArray[1].id;
        }
        return activePointId;
    };

    private getGradualInfo(gradFill?: IGradFill) {
        let { curGradualType } = this.value;
        let curGradualAngle: undefined | number = this.value.gradualAngle;
        let [curDirect] = this.directList;
        if (gradFill?.pathGradient) {
            const pathGrad = gradFill.pathGradient;
            const { path } = pathGrad;
            if (path === GradientType.Linear) {
                curGradualType = LINEAR_GRADIENT;
                if (gradFill?.lin) {
                    curGradualAngle = gradFill.lin.ang;
                }
                curDirect = getLinearDirection(curGradualAngle) as any;
            } else if (path === GradientType.Rect) {
                curGradualType = RECT_GRADIENT;
                curDirect = getRectDirection(pathGrad.fillToRect) as any;
            } else if (path === GradientType.Shape) {
                curGradualType = SHAPE_GRADIENT;
                curDirect = ShapeDirectionList[0] as any;
            } else {
                curGradualType = RADIAL_GRADIENT;
                curDirect = getConfigByRect(pathGrad.fillToRect);
            }
        }
        return { curGradualType, curGradualAngle, curDirect };
    }

    private handlePaletteGradualColorChange = (color: ITDColor, from: string) => {
        this.handleGradColorChange(color, from, false);
        // 上报数据
        this.getColorPickerManager()?.colorPickerTdwReport({
            color: color.srgb_color,
            colorPickerFrom: TDWVer6Type.COLOR_PICKER_DRAWER,
            colorGroupFrom: from,
            actionType: TDWVer5Type.COLOR_PICKER_FILL,
        });
    };

    private handleGradColorChange = (color: ITDColor, fromCustom, onlyChangeUI) => {
        this.getActivePoint().bgColor = color;
        this.value.presetItem = null;
        this.confirmGradChoice(onlyChangeUI);
    };
}
